import { Link } from 'react-router'
import { useAudio } from '../audio/AudioProvider'
import { formatDate, formatLength } from '../lib/format'
import type { TheorySummary } from '../lib/types'
import { CredibilityMeter } from './CredibilityMeter'
import { Icon } from './Icon'

interface TheoryCardProps {
  theory: TheorySummary
  /** Handed to the player so playback continues into the rest of the list. */
  queue?: TheorySummary[]
  featured?: boolean
}

export function TheoryCard({ theory, queue = [], featured = false }: TheoryCardProps) {
  const audio = useAudio()
  const isCurrent = audio.isCurrent(theory.slug)
  const hasAudio = Boolean(theory.audio?.url)
  const length = formatLength(theory.audio?.seconds)

  return (
    <article
      className={`theory-card${featured ? ' is-featured' : ''}${isCurrent ? ' is-playing' : ''}`}
    >
      {theory.coverImage && (
        <Link
          to={`/theory/${theory.slug}`}
          className="theory-card-media"
          tabIndex={-1}
          aria-hidden="true"
        >
          <img src={theory.coverImage} alt="" loading="lazy" />
        </Link>
      )}

      <div className="theory-card-body">
        <div className="theory-card-meta">
          <time
            className="eyebrow"
            dateTime={(theory.recordedAt ?? theory.publishedAt) ?? undefined}
          >
            {formatDate(theory.recordedAt ?? theory.publishedAt, 'short')}
          </time>
          {featured && <span className="badge badge-live">Featured</span>}
          {hasAudio && (
            <span className="badge badge-audio">
              <Icon name="headphones" size={12} />
              {length ?? 'Audio'}
            </span>
          )}
        </div>

        <h3 className="theory-card-title">
          {/* The stretched pseudo-element makes the whole card clickable while
              keeping the play button independently focusable. */}
          <Link to={`/theory/${theory.slug}`} className="stretched-link">
            {theory.title}
          </Link>
        </h3>

        {/* The quote sells it better than a description, when there is one. */}
        {theory.pullQuote ? (
          <p className="theory-card-quote">“{theory.pullQuote}”</p>
        ) : (
          theory.description && <p className="theory-card-summary">{theory.description}</p>
        )}

        <div className="theory-card-foot">
          <CredibilityMeter score={theory.credibility} size="compact" />

          <div className="theory-card-actions">
            {hasAudio && (
              <button
                type="button"
                className={`btn btn-sm play-pill${isCurrent && audio.playing ? ' is-playing' : ''}`}
                onClick={() => audio.play(theory, queue)}
                aria-label={
                  isCurrent && audio.playing ? `Pause ${theory.title}` : `Play ${theory.title}`
                }
              >
                <Icon name={isCurrent && audio.playing ? 'pause' : 'play'} size={14} />
                {isCurrent && audio.playing ? 'Playing' : 'Listen'}
              </button>
            )}
          </div>
        </div>

        {theory.tags.length > 0 && (
          <ul className="theory-card-tags" aria-label="Tags">
            {theory.tags.slice(0, 4).map((tag) => (
              <li key={tag}>
                <Link to={`/theories?tag=${encodeURIComponent(tag)}`} className="chip chip-tag">
                  {tag}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  )
}
